import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles, Users, MapPin, TrendingUp, Award, Calendar, CalendarClock, Star, Building2, Cpu, Database, Lightbulb, Handshake, Rocket, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import Section from "./Section";
import StartupCard from "./StartupCard";
import Cover from "./Cover";
import { Card } from "./ui/card";
import { pickLang, externalUrl } from "../lib/format";
import { mediaSrc } from "../pages/admin/MediaLibrary";
import { useCurrency } from "../lib/currency";

const GUILLEMET_OUVRE = "“";
const GUILLEMET_FERME = "”";

const useL = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
  return (v) => (v && typeof v === "object" ? (v[lang] ?? v.en ?? "") : (v ?? ""));
};
// Below this many declaring investors the total identifies them, so it stays hidden.
const MIN_DECLARING = 3;
const fundsPublic = (stats) =>
  (stats?.investors_declaring || 0) >= MIN_DECLARING && (stats?.investor_funds_usd || 0) > 0;

const statValue = (stats, field, fmtShort) => {
  if (!stats) return "—";
  // The profile promises an investor their own figure stays private, and with
  // one or two declaring, "the total" *is* their figure. Publish only once the
  // sum actually hides its contributors; "—" until then, never a bare 0.
  if (field === "funding") return fundsPublic(stats) ? fmtShort(stats.investor_funds_usd) : "—";
  return stats[field] ?? "—";
};

function Hero({ data, ctx }) {
  const L = useL();
  const { t } = useTranslation();
  const { fmtShort } = useCurrency();
  const stats = ctx.stats;
  return (
    <section className="relative hero-warm-wash bg-noise overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 fade-in-up">
          <Badge variant="outline" className="bg-card mb-6 inline-flex items-center gap-1.5 px-3 py-1">
            <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--accent))]" /><span className="text-xs font-medium">{L(data.eyebrow)}</span>
          </Badge>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight">{L(data.title)}</h1>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-xl">{L(data.subtitle)}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {data.primary && <Button asChild size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white btn-lift h-12 px-6" data-testid="home-hero-founder-cta"><Link to={data.primary.link || "/"}>{L(data.primary.label)} <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>}
            {data.secondary && <Button asChild size="lg" variant="outline" className="border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-white btn-lift h-12 px-6" data-testid="home-hero-investor-cta"><Link to={data.secondary.link || "/"}>{L(data.secondary.label)} <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>}
          </div>
          {stats && (
            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
              <span><strong className="text-foreground font-display">{stats.startups}</strong> {t("stats.startups")}</span>
              <span className="hidden sm:inline">·</span>
              <span><strong className="text-foreground font-display">{stats.countries}</strong> {t("stats.countries")}</span>
              <span className="hidden sm:inline">·</span>
              <span><strong className="text-foreground font-display">{fundsPublic(stats) ? fmtShort(stats.investor_funds_usd) : "—"}</strong> {t("stats.funding")}</span>
            </div>
          )}
        </div>
        <div className="lg:col-span-5 relative">
          <div className="relative grid grid-cols-2 gap-3">
            <div className="col-span-2 aspect-[4/3] rounded-2xl overflow-hidden border border-border shadow-sm">
              {data.image1 && <img src={mediaSrc(data.image1)} alt="" className="h-full w-full object-cover" fetchPriority="high" decoding="async" width="900" height="700" />}
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden border border-border shadow-sm">
              {data.image2 && <img src={mediaSrc(data.image2)} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" width="400" height="400" />}
            </div>
            <div className="aspect-square rounded-2xl overflow-hidden border border-border shadow-sm bg-card flex flex-col justify-center items-start p-5">
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{L(data.eyebrow)}</span>
              <span className="font-display text-3xl font-semibold mt-1">{stats?.startups ?? "—"}</span>
              <span className="text-xs text-muted-foreground mt-1">{t("stats.startups")}</span>
              <span className="mt-3 inline-flex items-center gap-1 text-xs text-[hsl(var(--accent))]"><TrendingUp className="h-3 w-3" /> Live in deal flow</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Logos({ data, ctx }) {
  const L = useL();
  const partners = ctx.partners || [];
  if (partners.length === 0) return null;
  return (
    <div className="border-y border-border bg-card/40">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-center text-xs uppercase tracking-[0.18em] text-muted-foreground mb-5">{L(data.heading)}</p>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-90">
          {partners.slice(0, 6).map((p) => <span key={p.partner_id} className="text-sm font-display font-semibold text-muted-foreground" data-testid="partner-logo">{p.name}</span>)}
        </div>
      </div>
    </div>
  );
}

function StatsBlock({ data, ctx }) {
  const L = useL();
  const { fmtShort } = useCurrency();
  const stats = ctx.stats;
  if (!stats) return null;
  const ICONS = { members: Users, countries: MapPin, startups: TrendingUp, funding: Award };
  return (
    <Section eyebrow={L(data.eyebrow)} title={L(data.title)} sub={L(data.subtitle)} className="py-14">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="impact-stats">
        {(data.items || []).map((it, i) => {
          const Icon = ICONS[it.field] || TrendingUp;
          return (
            <div key={i} className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center gap-2 text-[hsl(var(--accent))] text-xs uppercase tracking-wider">
                <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] inline-block" /><Icon className="h-4 w-4" />
              </div>
              <div className="font-display text-3xl sm:text-4xl font-semibold mt-2">{statValue(stats, it.field, fmtShort)}</div>
              <div className="text-sm text-muted-foreground mt-1">{L(it.label)}</div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function Featured({ data, ctx }) {
  const L = useL();
  return (
    <Section eyebrow={L(data.eyebrow)} title={L(data.title)} sub={L(data.subtitle)}>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {(ctx.startups || []).map((s) => <StartupCard key={s.startup_id} s={s} />)}
      </div>
      {data.cta && (
        <div className="mt-10 flex justify-center">
          <Button asChild variant="outline" className="border-[hsl(var(--primary))] text-[hsl(var(--primary))]" data-testid="home-see-all-startups">
            <Link to={data.cta.link || "/investors"}>{L(data.cta.label)} <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      )}
    </Section>
  );
}

// Les icones offertes aux cartes, et « none » pour une carte sans icone.
const CARD_ICONS = { sparkles: Sparkles, database: Database, cpu: Cpu, lightbulb: Lightbulb, users: Users, award: Award, trending: TrendingUp, map: MapPin, calendar: Calendar, building: Building2, handshake: Handshake, rocket: Rocket, shield: ShieldCheck };

function CardIcon({ name }) {
  if (name === "none") return null;
  const I = CARD_ICONS[name] || Sparkles;
  return (
    <div className="h-10 w-10 rounded-lg bg-[hsl(var(--secondary))] text-[hsl(var(--primary))] flex items-center justify-center mb-4"><I className="h-5 w-5" /></div>
  );
}

// Sans consigne, quatre colonnes : c'est ce que les pages existantes attendent.
const gridCols = (n) => (String(n) === "3" ? "md:grid-cols-3" : String(n) === "2" ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-4");

function Cards({ data }) {
  const L = useL();
  const { t } = useTranslation();
  const items = data.items || [];
  if (data.variant === "features") {
    return (
      <Section eyebrow={L(data.eyebrow)} title={L(data.title)} sub={L(data.subtitle)}>
        <div className={`grid gap-5 ${gridCols(data.columns)}`}>
          {items.map((it, i) => (
            <div key={i} className="rounded-xl border border-border bg-card p-6 card-hover">
              <CardIcon name={it.icon} />
              <h3 className="font-display text-lg font-semibold mb-1.5">{L(it.title)}</h3>
              <p className="text-sm text-muted-foreground">{L(it.desc)}</p>
            </div>
          ))}
        </div>
      </Section>
    );
  }
  return (
    <Section eyebrow={L(data.eyebrow)} title={L(data.title)} sub={L(data.subtitle)}>
      <div className={`grid gap-4 ${gridCols(data.columns)}`}>
        {items.map((it, i) => {
          const accent = it.tone === "accent" ? "text-[hsl(var(--accent))]" : it.tone === "primary" ? "text-[hsl(var(--primary))]" : "text-foreground";
          return (
            <Link key={i} to={it.link || "/"} className="group rounded-xl border border-border bg-card p-6 card-hover flex flex-col gap-3">
              <h3 className={`font-display text-lg font-semibold ${accent}`}>{L(it.title)}</h3>
              <p className="text-sm text-muted-foreground flex-1">{L(it.desc)}</p>
              <span className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--primary))] group-hover:gap-2 transition-all">{t("paths.explore")} <ArrowRight className="h-3 w-3" /></span>
            </Link>
          );
        })}
      </div>
    </Section>
  );
}


// En-tête de page : ce que /about, /sme ou /founders affichent en haut. Le bloc
// « hero » ne convient pas ici — il embarque le compteur de startups et la
// mention « deal flow » de l'accueil.
function PageHero({ data }) {
  const L = useL();
  const centre = data.align === "center";
  return (
    <section className="relative hero-warm-wash bg-noise">
      <div className={`mx-auto px-4 sm:px-6 lg:px-8 ${centre ? "max-w-4xl text-center pt-20 pb-10" : "max-w-7xl grid lg:grid-cols-12 gap-10 items-center pt-16 pb-12"}`}>
        <div className={centre ? "" : "lg:col-span-7"}>
          {L(data.eyebrow) && <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))] mb-3">{L(data.eyebrow)}</p>}
          <h1 className={`font-display text-4xl sm:text-5xl font-semibold leading-tight ${centre ? "lg:text-6xl" : ""}`}>{L(data.title)}</h1>
          {L(data.subtitle) && <p className={`mt-4 text-muted-foreground ${centre ? "text-lg" : "max-w-xl"}`}>{L(data.subtitle)}</p>}
          {data.cta && L(data.cta.label) && (
            <div className={`mt-7 flex flex-wrap gap-3 ${centre ? "justify-center" : ""}`}>
              <Button asChild size="lg" className="bg-[hsl(var(--accent))] text-white" data-testid="page-hero-cta">
                <Link to={data.cta.link || "/"}>{L(data.cta.label)} <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          )}
        </div>
        {!centre && data.image && (
          <div className="lg:col-span-5">
            <img src={mediaSrc(data.image)} alt="" className="rounded-2xl border border-border shadow-sm w-full object-cover aspect-[4/3]" loading="lazy" />
          </div>
        )}
        {centre && data.image && (
          <img src={mediaSrc(data.image)} alt="" className="mt-10 rounded-2xl border border-border shadow-sm w-full object-cover aspect-[16/9]" loading="lazy" />
        )}
      </div>
    </section>
  );
}

function RichText({ data }) {
  const L = useL();
  const style = data.style || "";
  // Une ligne vide separe deux paragraphes, comme dans un traitement de texte.
  const paras = L(data.body).split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const cls = style === "quote"
    ? "font-serif italic text-2xl text-foreground/90 leading-relaxed"
    : style === "serif"
      ? "font-serif text-lg text-foreground/90 leading-relaxed whitespace-pre-line"
      : "text-foreground/85 leading-relaxed whitespace-pre-line";
  const texte = (
    <div className="space-y-5">
      {paras.map((p, i) => <p key={i} className={cls}>{style === "quote" ? GUILLEMET_OUVRE + p + GUILLEMET_FERME : p}</p>)}
    </div>
  );
  return (
    <Section eyebrow={L(data.eyebrow)} title={L(data.title)}>
      {data.image ? (
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">{texte}</div>
          <div className="lg:col-span-5">
            <img src={mediaSrc(data.image)} alt="" className="rounded-2xl border border-border shadow-sm w-full h-auto" loading="lazy" />
          </div>
        </div>
      ) : (
        <div className="max-w-3xl">{texte}</div>
      )}
    </Section>
  );
}

function CtaBand({ data }) {
  const L = useL();
  return (
    <Section className="py-14">
      <div className="rounded-2xl bg-[hsl(var(--primary))] text-white p-8 sm:p-12 text-center">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold">{L(data.title)}</h2>
        {data.subtitle && <p className="mt-2 text-white/80 max-w-xl mx-auto">{L(data.subtitle)}</p>}
        {data.cta && <Button asChild size="lg" className="mt-6 bg-white text-[hsl(var(--primary))] hover:bg-white/90"><Link to={data.cta.link || "/"}>{L(data.cta.label)}</Link></Button>}
      </div>
    </Section>
  );
}

function Testimonial({ data }) {
  const L = useL();
  return (
    <Section className="py-14">
      <blockquote className="max-w-3xl mx-auto text-center">
        <p className="font-display text-xl sm:text-2xl leading-relaxed">“{L(data.quote)}”</p>
        <footer className="mt-4 text-sm text-muted-foreground">{data.author}{data.role ? ` · ${data.role}` : ""}</footer>
      </blockquote>
    </Section>
  );
}


// ---------------------------------------------------------------- Événements
const isPastEvent = (e, today) => e.ended ?? !!((e.end_date || e.date) && (e.end_date || e.date) < today);

function EventsBlock({ data, ctx }) {
  const L = useL();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
  const today = new Date().toISOString().slice(0, 10);
  const fmt = (d) => { const x = new Date(d); return isNaN(x) ? "" : x.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "long" }); };
  const when = (e) => (e.end_date && e.end_date !== e.date
    ? t("events.date_range", { from: fmt(e.date), to: fmt(e.end_date) })
    : fmt(e.date));

  // The server already sorts soonest first; here we only drop what is over.
  const upcoming = (ctx.events || []).filter((e) => !isPastEvent(e, today));
  if (!upcoming.length) return null;
  // At most one event is featured — the CMS enforces it — and it leads.
  const star = upcoming.find((e) => e.featured);
  const rest = upcoming.filter((e) => e !== star).slice(0, star ? 3 : 4);

  return (
    <Section eyebrow={L(data.eyebrow)} title={L(data.title)} sub={L(data.subtitle)}>
      {star && (
        <Link to={`/events/${star.event_id}`} className="block mb-6" data-testid="home-featured-event">
          <Card className="overflow-hidden card-hover bg-card grid md:grid-cols-5">
            <div className="md:col-span-2 aspect-[16/9] md:aspect-auto md:min-h-[220px] bg-secondary overflow-hidden">
              <Cover src={star.image_url ? mediaSrc(star.image_url) : ""} seed={star.slug || star.event_id}
                     label={pickLang(star.title, lang)} alt={pickLang(star.title, lang)} testid="event-cover-generated" />
            </div>
            <div className="md:col-span-3 p-6 flex flex-col gap-2 justify-center">
              <Badge className="w-fit bg-[hsl(var(--accent))] text-white inline-flex items-center gap-1">
                <Star className="h-3 w-3" /> {t("home.featured_event")}
              </Badge>
              <h3 className="font-display text-xl sm:text-2xl font-semibold leading-snug">{pickLang(star.title, lang)}</h3>
              <p className="text-sm text-foreground/80 line-clamp-2">{pickLang(star.description, lang)}</p>
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-1">
                {star.date && <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {when(star)}</span>}
                {star.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {star.location}</span>}
              </div>
            </div>
          </Card>
        </Link>
      )}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {rest.map((e) => (
          <Link key={e.event_id} to={`/events/${e.event_id}`} data-testid="home-event-card">
            <Card className="card-hover overflow-hidden h-full flex flex-col bg-card">
              <div className="aspect-[16/9] bg-secondary overflow-hidden">
                <Cover src={e.image_url ? mediaSrc(e.image_url) : ""} seed={e.slug || e.event_id}
                       label={pickLang(e.title, lang)} alt={pickLang(e.title, lang)} testid="event-cover-generated" />
              </div>
              <div className="p-5 flex flex-col gap-2 flex-1">
                {e.type && <Badge variant="secondary" className="capitalize w-fit">{e.type}</Badge>}
                <h3 className="font-display font-semibold leading-snug">{pickLang(e.title, lang)}</h3>
                <div className="mt-auto pt-2 space-y-1 text-sm text-muted-foreground">
                  {e.date && <p className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {when(e)}</p>}
                  {e.location && <p className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {e.location}</p>}
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
      {data.cta && (
        <div className="mt-10 flex justify-center">
          <Button asChild variant="outline" className="border-[hsl(var(--primary))] text-[hsl(var(--primary))]" data-testid="home-see-all-events">
            <Link to={data.cta.link || "/events"}>{L(data.cta.label)} <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      )}
    </Section>
  );
}

// ------------------------------------------------------------- Opportunités
function OpportunitiesBlock({ data, ctx }) {
  const L = useL();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
  const fmt = (d) => { const x = new Date(d); return isNaN(x) ? "" : x.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "short", year: "numeric" }); };

  // Both boards, one shape — the same two collections the /opportunites page keeps
  // apart on purpose. Closed calls never make the home page.
  const items = [
    ...(ctx.funding || []).map((f) => ({
      key: `f-${f.funding_id}`, kind: "funding", title: pickLang(f.title, lang), org: f.funder,
      deadline: f.deadline, rolling: f.rolling, is_closed: !!f.is_closed, created_at: f.created_at,
      href: externalUrl(f.apply_url), external: true, country: f.country,
    })),
    ...(ctx.programs || []).map((p) => ({
      key: `p-${p.program_id}`, kind: "program", title: pickLang(p.title, lang), org: p.published_by,
      deadline: p.deadline, rolling: p.rolling, is_closed: !!p.is_closed, created_at: p.created_at,
      href: `/programs/${p.slug || p.program_id}`, external: false, country: p.location,
    })),
  ].filter((o) => !o.is_closed)
   .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))
   .slice(0, 3);
  if (!items.length) return null;

  return (
    <Section eyebrow={L(data.eyebrow)} title={L(data.title)} sub={L(data.subtitle)}>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((o) => {
          const inner = (
            <Card className="card-hover h-full flex flex-col gap-2 p-5 bg-card" data-testid="home-opportunity-card">
              <Badge variant="secondary" className="w-fit">
                {o.kind === "program" ? t("opportunities.kind_program_one") : t("opportunities.kind_funding_one")}
              </Badge>
              <h3 className="font-display font-semibold leading-snug line-clamp-3">{o.title}</h3>
              {o.org && <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 shrink-0" /> {o.org}</p>}
              <p className="mt-auto pt-2 text-xs inline-flex items-center gap-1.5 text-[hsl(var(--accent))]">
                <CalendarClock className="h-3.5 w-3.5" />
                {o.deadline ? `${t("funding.closes")} ${fmt(o.deadline)}` : o.rolling ? t("funding.rolling") : t("programs.dates_tbc")}
              </p>
            </Card>
          );
          return o.external && o.href
            ? <a key={o.key} href={o.href} target="_blank" rel="noreferrer">{inner}</a>
            : <Link key={o.key} to={o.href}>{inner}</Link>;
        })}
      </div>
      {data.cta && (
        <div className="mt-10 flex justify-center">
          <Button asChild variant="outline" className="border-[hsl(var(--primary))] text-[hsl(var(--primary))]" data-testid="home-see-all-opportunities">
            <Link to={data.cta.link || "/opportunites"}>{L(data.cta.label)} <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      )}
    </Section>
  );
}

const REGISTRY = { hero: Hero, page_hero: PageHero, logos: Logos, stats: StatsBlock, featured: Featured, cards: Cards, rich_text: RichText, cta_band: CtaBand, testimonial: Testimonial, events: EventsBlock, opportunities: OpportunitiesBlock };

export default function BlockRenderer({ blocks, ctx = {} }) {
  return (
    <>
      {(blocks || []).map((b) => {
        const C = REGISTRY[b.type];
        return C ? <C key={b.block_id} data={b.data || {}} ctx={ctx} /> : null;
      })}
    </>
  );
}
